import React, { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
const FAQ = () => {
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);
  const toggleAccordion = (id: string) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };
  const faqs = [{
    id: 'faq1',
    question: 'What makes Spora different from other mushroom products?',
    answer: 'Spora uses a dual-extraction method with alcohol pre-soak and hot water infusion to maximize the bioavailability of both water- and alcohol-soluble compounds. Most mushroom products only extract water-soluble compounds, missing up to 30-40% of the beneficial components. Additionally, our carefully selected spirit pairings enhance both the flavour profile and the extraction efficiency.'
  }, {
    id: 'faq2',
    question: 'Is there alcohol in the final drink?',
    answer: 'When prepared as directed with an uncovered brewing process, the final Spora beverage contains less than 0.5% ABV, which is considered non-alcoholic by regulatory standards. This is comparable to the natural alcohol content in kombucha or ripe fruit. The brewing process is specifically designed to allow the alcohol to evaporate while leaving the beneficial compounds behind.'
  }, {
    id: 'faq3',
    question: 'How often should I drink Spora?',
    answer: 'We recommend one serving (8 oz) daily for optimal benefits. The effects of medicinal mushrooms are cumulative, with many users reporting enhanced benefits after 2-3 weeks of consistent use. You can adjust based on your individual needs – some people prefer to have different varieties at different times of day (e.g., Cordyceps in the morning, Reishi in the evening).'
  }, {
    id: 'faq4',
    question: 'Are there any side effects?',
    answer: 'Medicinal mushrooms have been used safely for centuries. However, as with any supplement, individuals may respond differently. Some people may experience mild digestive adjustment when first starting. If you have medical conditions, are pregnant or nursing, or are taking medications, we recommend consulting with a healthcare professional before use.'
  }, {
    id: 'faq5',
    question: 'How should I store my Spora products?',
    answer: 'Store all Spora products in a cool, dry place, away from direct sunlight. Pods and powder do not require refrigeration and will maintain potency for up to 18 months when properly stored. Once prepared, Spora beverages are best consumed immediately, but can be refrigerated for up to 24 hours.'
  }, {
    id: 'faq6',
    question: 'Why is Spora called the Third Liquid Culture?',
    answer: 'Because tea and coffee shaped the first two great daily rituals – balance and energy. Spora represents the third: a full-spectrum infusion delivering antioxidants, immunonutrients, and unique system benefits only mushrooms provide.'
  }, {
    id: 'faq7',
    question: 'How does Spora taste?',
    answer: 'Each variety has its own profile. Some are bright and botanical, others earthy or smoky. The spirit pairing adds subtle complexity — like a fine tea or crafted cocktail — without leaving alcohol in the final drink.'
  }, {
    id: 'faq8',
    question: 'Why is alcohol part of the process?',
    answer: 'Many beneficial mushroom compounds are insoluble in water. A small amount of alcohol unlocks these bioactives, then evaporates during brewing — leaving complexity of flavor but virtually no alcohol in the final cup.'
  }, {
    id: 'faq9',
    question: 'What health benefits does Spora provide compared to coffee or tea?',
    answer: 'Coffee delivers caffeine. Tea delivers antioxidants. Spora delivers antioxidants + immunonutrients, plus a unique third pillar depending on the mushroom – such as stamina, cognition, stress relief, or metabolic balance.'
  }, {
    id: 'faq10',
    question: 'Is Spora considered an adaptogen?',
    answer: "Certain varieties, like Reishi, have classic adaptogenic effects. But Spora, in its entirety, is broader: it's not just about stress balance, but about fortifying multiple systems of the body at once."
  }, {
    id: 'faq11',
    question: 'Who is Spora for?',
    answer: "Spora is for anyone seeking a daily ritual that supports long-term health. It's ideal for: people who want the focus of coffee or the calm of tea, but with deeper, functional benefits for immunity, resilience, and restoration."
  }];
  return <div className="w-full bg-white pt-24" data-id="element-531">
      <div className="container mx-auto px-4 py-16" data-id="element-532">
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-16" data-id="element-533">
          Frequently Asked Questions
        </h1>
        {/* FAQ Section */}
        <section className="mb-16" data-id="element-534">
          <div className="max-w-3xl mx-auto space-y-4" data-id="element-535">
            {faqs.map(faq => <div key={faq.id} className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-536">
                <button className="flex justify-between items-center w-full p-4 text-left" onClick={() => toggleAccordion(faq.id)} data-id="element-537">
                  <span className="font-medium" data-id="element-538">{faq.question}</span>
                  <ChevronDownIcon className={`w-5 h-5 transition-transform ${activeAccordion === faq.id ? 'rotate-180' : ''}`} data-id="element-539" />
                </button>
                {activeAccordion === faq.id && <div className="p-4 pt-0 text-gray-600" data-id="element-540">
                    <p data-id="element-541">{faq.answer}</p>
                  </div>}
              </div>)}
          </div>
        </section>
        {/* Contact Form */}
        <section className="max-w-2xl mx-auto" data-id="element-542">
          <h2 className="text-3xl font-bold text-center mb-8" data-id="element-543">Contact Us</h2>
          <p className="text-gray-600 text-center mb-12" data-id="element-544">
            Have a question that's not answered above? We're here to help.
          </p>
          <form className="space-y-6" data-id="element-545">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-546">
              <div data-id="element-547">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-548">
                  Name
                </label>
                <input type="text" id="name" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black" placeholder="Your name" data-id="element-549" />
              </div>
              <div data-id="element-550">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-551">
                  Email
                </label>
                <input type="email" id="email" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black" placeholder="Your email address" data-id="element-552" />
              </div>
            </div>
            <div data-id="element-553">
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-554">
                Subject
              </label>
              <select id="subject" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black appearance-none bg-white" data-id="element-555">
                <option value="" data-id="element-556">Select a subject</option>
                <option value="research" data-id="element-557">Research Inquiry</option>
                <option value="product" data-id="element-558">Product Question</option>
                <option value="collaboration" data-id="element-559">Collaboration Opportunity</option>
                <option value="other" data-id="element-560">Other</option>
              </select>
            </div>
            <div data-id="element-561">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-562">
                Message
              </label>
              <textarea id="message" rows={6} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black" placeholder="How can we help you?" data-id="element-563"></textarea>
            </div>
            <button type="submit" className="w-full bg-black text-white py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors" data-id="element-564">
              Send Message
            </button>
          </form>
        </section>
      </div>
    </div>;
};
export default FAQ;